"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";

export function Header() {
  // For mobile menu toggle
  const [isOpen, setIsOpen] = useState(false);
  // For click behavior on mobile/tablet
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  // Used to detect if we're on mobile/tablet for click vs hover behavior
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile on component mount
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    // Initialize on first render
    checkIfMobile();

    // Update on window resize
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Close mega menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        megaMenuRef.current &&
        !megaMenuRef.current.contains(event.target as Node)
      ) {
        setMegaMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mega menu when navigating
  useEffect(() => {
    setMegaMenuOpen(false);
  }, [pathname]);

  // Service data for the mega menu
  const serviceItems = [
    {
      title: "Montage & Demontage",
      url: "/krantjanster/montage-demontage",
      description:
        "Installation och demontering av kranar och lyftsystem med högsta precision.",
      image: "/images/montage-demontage.webp",
    },
    {
      title: "Service & Underhåll",
      url: "/krantjanster/service-underhall",
      description:
        "Förebyggande underhåll och service för optimal drift och säkerhet.",
      image: "/images/service-underhall.webp",
    },
    {
      title: "Reparation",
      url: "/krantjanster/reparation",
      description:
        "Snabb och effektiv felsökning och reparation med minimal stilleståndstid.",
      image: "/images/kranreparation.webp",
    },
    {
      title: "Radiosystem",
      url: "/krantjanster/radiosystem",
      description:
        "Installation och service av moderna radiosystem för effektiv kranstyrning.",
      image: "/images/radiostyrning.webp",
    },
    {
      title: "Svetsning & Smide",
      url: "/krantjanster/svetsning-smide",
      description:
        "Kvalificerad svetsning och smide för reparationer och specialanpassningar.",
      image: "/images/svetsning-smide.webp",
    },
  ];

  const navigation = [
    { name: "Hem", href: "/" },
    { name: "Krantjänster", href: "/krantjanster" },
    { name: "Kranar & Lyftanordningar", href: "/kranar-lyftanordningar" },
    { name: "Projektgalleri", href: "/projektgalleri" },
    { name: "Om Axfalk", href: "/om-axfalk" },
    { name: "Kontakta Oss", href: "/kontakta-oss" },
  ];

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center justify-center h-16">
            <Image
              src="/logo.png"
              alt="Axfalk AB"
              width={130}
              height={40}
              priority
              className="object-contain my-auto"
            />
          </Link>

          {/* Desktop Navigation - only on large screens and up */}
          <nav className="hidden lg:flex items-center space-x-8">
            {[
              ...navigation.map((item) =>
                item.name === "Krantjänster" ? (
                  <div
                    key={item.name}
                    ref={megaMenuRef}
                    className="relative group"
                  >
                    {/* Invisible element to bridge the gap between button and menu */}
                    <div className="hidden lg:block absolute h-8 bottom-0 left-0 w-full translate-y-full"></div>

                    <button
                      className={`text-sm font-medium transition-colors hover:text-primary flex items-center gap-1 ${
                        pathname === item.href ||
                        pathname.startsWith("/krantjanster/")
                          ? "text-primary"
                          : "text-gray-700"
                      }`}
                      aria-haspopup="true"
                      aria-expanded={megaMenuOpen}
                      aria-controls="mega-menu"
                      onClick={() => setMegaMenuOpen(!megaMenuOpen)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setMegaMenuOpen(!megaMenuOpen);
                        }
                      }}
                    >
                      {item.name}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          megaMenuOpen
                            ? "rotate-180"
                            : "group-hover:lg:rotate-180"
                        }`}
                        aria-hidden="true"
                      />
                    </button>

                    {/* Mega Menu - visible on click (mobile) or hover (desktop) */}
                    <div
                      id="mega-menu"
                      className={`absolute top-full -left-64 md:-left-80 lg:-left-80 mt-6 w-[90vw] max-w-[700px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50 transition-opacity duration-150 ${
                        megaMenuOpen
                          ? "opacity-100 visible"
                          : "opacity-0 invisible lg:group-hover:opacity-100 lg:group-hover:visible"
                      }`}
                      role="menu"
                    >
                      <div className="grid grid-cols-2 gap-6 p-6">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900 mb-3">
                            Våra tjänster
                          </h3>
                          <p className="text-gray-600 text-sm mb-4">
                            Kompletta tjänster för kranar och lyftsystem med
                            över 40 års erfarenhet
                          </p>
                          <Link
                            href="/krantjanster"
                            className="inline-flex items-center gap-2 px-4 py-2 mt-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-sm font-medium hover:bg-blue-100 hover:text-blue-800 transition-colors"
                          >
                            <span>Se alla tjänster</span>{" "}
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </div>

                        <div className="space-y-3">
                          {serviceItems.map((service) => (
                            <Link
                              key={service.url}
                              href={service.url}
                              className="flex items-center gap-3 rounded-md p-2 hover:bg-blue-50 transition-colors"
                              role="menuitem"
                            >
                              <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 border border-gray-200">
                                <div className="relative h-full w-full">
                                  <Image
                                    src={service.image}
                                    alt={service.title}
                                    fill
                                    className="object-cover"
                                    sizes="48px"
                                  />
                                </div>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900 block">
                                  {service.title}
                                </span>
                                <p className="text-xs text-gray-600 line-clamp-1">
                                  {service.description}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>

                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`text-sm font-medium transition-colors hover:text-primary ${
                      pathname === item.href ? "text-primary" : "text-gray-700"
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ),
              <Link
                key="contact-us"
                href="/kontakta-oss"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 border-2 border-blue-600 shadow-md hover:shadow-lg no-underline"
              >
                Få Offert
                <ArrowRight className="h-4 w-4" />
              </Link>,
            ]}
          </nav>

          {/* Mobile/tablet menu button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button
                onClick={() => setIsOpen(true)}
                className="p-2 rounded-md text-gray-700 hover:text-primary hover:bg-gray-100 transition-colors"
                aria-label="Öppna meny"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[300px] sm:w-[400px] p-0 overflow-y-auto [&>button.absolute.right-4.top-4]:hidden"
            >
              <SheetTitle className="sr-only">Huvudmeny</SheetTitle>
              <div className="flex flex-col h-full overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-lg">A</span>
                    </div>
                    <span className="text-xl font-bold text-gray-900">
                      Axfalk AB
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      href="/kontakta-oss"
                      onClick={() => setIsOpen(false)}
                      className="hidden sm:inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-all duration-200 border border-blue-600 shadow-sm no-underline"
                    >
                      Få Offert
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-2 rounded-md text-gray-700 hover:text-primary hover:bg-gray-100 transition-colors"
                      aria-label="Stäng meny"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-6 py-6">
                  <div className="space-y-2">
                    {navigation.map((item) => {
                      if (item.name === "Krantjänster") {
                        return (
                          <div key={item.name} className="space-y-2">
                            <Link
                              href={item.href}
                              onClick={() => setIsOpen(false)}
                              className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                                pathname === item.href
                                  ? "bg-blue-50 text-blue-600 border-l-4 border-blue-600"
                                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                              }`}
                            >
                              {item.name}
                            </Link>

                            {/* Service submenu items for mobile */}
                            <div className="pl-6 space-y-1 border-l-2 border-blue-100 ml-4">
                              {serviceItems.map((service) => (
                                <Link
                                  key={service.url}
                                  href={service.url}
                                  onClick={() => setIsOpen(false)}
                                  className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    pathname === service.url
                                      ? "bg-blue-50 text-blue-600"
                                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                                  }`}
                                >
                                  {service.title}
                                </Link>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <Link
                          key={item.name}
                          href={item.href}
                          onClick={() => setIsOpen(false)}
                          className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                            pathname === item.href
                              ? "bg-blue-50 text-blue-600 border-l-4 border-blue-600"
                              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          }`}
                        >
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                </nav>

                {/* CTA */}
                <div className="p-6 border-t border-gray-200">
                  <Link
                    href="/kontakta-oss"
                    onClick={() => setIsOpen(false)}
                    className="inline-flex items-center justify-center gap-2 w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 border-2 border-blue-600 shadow-md hover:shadow-lg no-underline"
                  >
                    Få Kostnadsfri Offert
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
